import { AlertTriangle, ArrowRight, Loader2, MapPin, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { needsLocalCatchment } from "@/lib/heuristics";
import { PresetLoader } from "@/components/iv/intake/PresetLoader";
import { CountryCombobox } from "@/components/iv/intake/CountryCombobox";
import { ValidationStatusCard } from "@/components/iv/intake/ValidationStatusCard";

const MIN = 50;
const MAX = 1500;
const field = "w-full rounded-xl border border-white/10 bg-[#060913]/70 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] focus:border-cyan-400/60 focus:shadow-[0_0_0_3px_rgba(6,182,212,0.15)]";
const label = "mb-1.5 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-300";

export const IntakePanel = ({ form, setForm, onSubmit, busy, error, result }) => {
  const { user, quota } = useApp();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const len = form.idea_text.trim().length;
  const exhausted = !!user && quota?.remaining === 0;
  const valid = len >= MIN && len <= MAX && !!form.country_code && !exhausted;
  const local = needsLocalCatchment(form.idea_text) || result?.classification?.needs_local_catchment;
  const inferred = result?.classification?.is_ai_inferred ? result.classification.inferred_target_audience : null;
  const chooseCountry = (code) => setForm((f) => ({ ...f, country_code: code }));

  return (
    <form className="iv-glass space-y-5 rounded-2xl p-4 sm:p-6" onSubmit={(e) => { e.preventDefault(); if (valid && !busy) onSubmit(); }} data-testid="intake-form">
      <PresetLoader onLoad={(data) => setForm({ ...data, idea_text: data.idea_text.slice(0, MAX) })} />
      <div>
        <label htmlFor="idea-text" className={label}>Idea description <span className="text-rose-300">*</span></label>
        <textarea id="idea-text" rows={6} maxLength={MAX} value={form.idea_text}
          onChange={(e) => setForm((f) => ({ ...f, idea_text: e.target.value.slice(0, MAX) }))} className={`${field} noscrollbar resize-y`}
          placeholder="What are you building, for whom, and how will it make money?" data-testid="intake-idea-textarea" />
        <p className={`mt-1 text-right font-mono text-[11px] ${len > MAX ? "text-rose-300" : len < MIN ? "text-amber-300" : "text-emerald-300"}`} data-testid="intake-idea-counter">
          {len.toLocaleString()} / 1,500 {len < MIN && `· ${MIN - len} more to go`}
        </p>
      </div>
      <CountryCombobox value={form.country_code} onChange={chooseCountry} labelClass={label} />
      <div className={`rounded-xl transition-[box-shadow,background-color] ${local ? "bg-amber-500/5 p-3 shadow-[0_0_0_1px_rgba(245,158,11,0.45)]" : ""}`} data-testid="intake-catchment-wrapper" data-highlighted={local ? "true" : "false"}>
        <label htmlFor="catchment" className={label}>
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> Local city / pin-code catchment
          {local && <span className="rounded-full border border-amber-400/40 px-2 py-0.5 text-[10px] normal-case tracking-normal text-amber-300" data-testid="intake-catchment-recommended">Recommended — physical / hyperlocal idea</span>}
        </label>
        <input id="catchment" value={form.city_region} onChange={set("city_region")} className={field} maxLength={200}
          placeholder="e.g. Bengaluru 560102 - HSR Layout Sectors 1–7" data-testid="intake-catchment-input" />
      </div>
      <div>
        <label htmlFor="audience" className={label}>Target audience <span className="normal-case tracking-normal text-slate-500">(optional)</span></label>
        <input id="audience" value={form.target_audience} onChange={set("target_audience")} className={field} maxLength={500}
          placeholder="Leave blank and AI infers the highest-margin M.A.N. buyer segment" data-testid="intake-audience-input" />
        {inferred && (
          <p className="mt-2 flex flex-wrap items-start gap-2 text-xs text-slate-300" data-testid="intake-ai-inferred-audience">
            <span className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold text-violet-200">
              <Sparkles className="h-3 w-3" aria-hidden="true" /> AI-Inferred
            </span>
            <span className="min-w-0 flex-1">{inferred}</span>
          </p>
        )}
      </div>
      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-xl border border-rose-400/40 bg-rose-500/10 p-3 text-sm text-rose-200" data-testid="intake-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {error}
        </p>
      )}
      {result && <ValidationStatusCard result={result} />}
      {exhausted && !result && (
        <p className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="intake-quota-exhausted">
          You've used your free assessment. Your idea is still saved here.
        </p>
      )}
      <button type="submit" disabled={!valid || busy}
        className="iv-cta inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
        data-testid="intake-run-validation-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        {busy ? "Classifying your idea…" : "Run All 20 Validation Criteria"} {!busy && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
      </button>
    </form>
  );
};
